/* eslint-disable max-lines-per-function */
import { cn } from "tailwind-variants";
import CheckCircleIcon from "assets/icons/check-circle-broken.svg?vue";
import CheckIcon from "assets/icons/check.svg?vue";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { renderList } from "common/vue/render-list";

export default function ArtistEditsInformation({ isMobile = false, onClose, ...restProps }) {
  const beforeImage = remoteImageStaticUrl("2/gen/user-retention-modal-pin-before.png", {
    w: 275,
    h: 275
  });

  const afterImage = remoteImageStaticUrl("2/gen/user-retention-modal-pin-after.png", {
    w: 275,
    h: 275
  });

  const sampleMessages = [
    'Add the text "#1 Fan" in white color onto the blue flag.',
    "Change the color of the dog's t-shirt from blue to red."
  ];

  const artistEdits = ["Text Added", "Color Changed"];

  const features = [
    "<24 hour turnaround",
    "Direct print, fast delivery",
    "Background removal",
    "Change color",
    "Add text"
  ];

  return (
    <div
      {...restProps}
      className={cn("space-y-4", restProps.className)}
      data-testid="artist-edits-info"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Artist Edits - How it Works?</h2>
          <p className="text-sm text-gray-600">Human artist updates within 24 hours.</p>
        </div>
        {isMobile ? (
          <button
            className="cursor-pointer self-start border-0 bg-transparent p-0"
            type="button"
            data-testid="artist-edits-info-close-button"
            onClick={() => onClose?.()}
          >
            <UseSvg className="h-5 w-5" icon="menu-close" data-testid="h-5" />
          </button>
        ) : null}
      </div>
      <div className="relative flex rounded-xl border-[3px] border-gray-200 p-1.5">
        <div className="absolute left-1/2 top-1/3 z-10 -translate-x-1/2">
          <div className="flex size-8 items-center justify-center rounded-full bg-black shadow-md">
            <UseSvg className="h-5 w-5 text-white" icon="arrow-right-long" data-testid="h-5-1" />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-3 p-2">
          <div className="flex flex-col items-center gap-2">
            <p className="text-center text-sm font-bold">Original Upload</p>
            <img
              className="w-full max-w-[180px] object-cover"
              src={beforeImage}
              alt="Before artist edits"
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-bold text-gray-800">Sample message to Artist:</p>
            {renderList(sampleMessages, (message, index) => (
              <div
                className="flex items-start gap-2"
                key={message}
                data-testid={`sample-message-${index}`}
              >
                <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  {index + 1}
                </div>
                <span className="mb-0 text-sm leading-tight text-gray-600">{message}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-3 rounded-lg bg-[#E3F1FF] p-2">
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <p className="text-center text-sm font-bold">Artist Edited</p>
              <span
                className="rounded-full bg-green-700 px-1.5 py-0.5 text-xs font-medium text-white"
                data-testid="artist-edits-free-badge"
              >
                {" "}
                Free{" "}
              </span>
            </div>
            <img
              className="w-full max-w-[180px] object-cover"
              src={afterImage}
              alt="After artist edits"
            />
          </div>
          <div className="space-y-2">
            <p className="text-center text-sm font-bold text-gray-800">Artist Edits:</p>
            <div className="flex justify-center">
              <div className="flex flex-col gap-2">
                {renderList(artistEdits, edit => (
                  <div
                    className="flex items-center gap-2"
                    key={edit}
                    data-testid={`artist-edit-${edit}`}
                  >
                    <CheckCircleIcon
                      className="size-6 shrink-0 text-green-700"
                      data-testid="size-6"
                    />

                    <span className="text-sm font-bold text-gray-800">{edit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <p className="text-base font-bold">What else do you get with Artist Edit?</p>
        <div className="grid grid-cols-2 gap-2">
          {renderList(features, feature => (
            <div
              className="flex items-center gap-2 text-xs font-normal text-gray-900 lg:text-sm"
              key={feature}
            >
              <CheckIcon className="size-6 shrink-0 text-green-700" data-testid="size-6-1" />
              <span className="mb-0 text-sm leading-tight text-gray-600">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

ArtistEditsInformation.displayName = "ArtistEditsInformation";
